import { ServiceWithPricing } from '@/util/types/serverTypes';
import { RequiredInfoValue } from '@/util/types/serverTypes';
import { AddressForScheduler } from '@/util/types/globalTypes';
import { ServWithMods, EquipmentQuantity } from '@/util/types/serverTypes';
import { modifierPriceAdjFn } from '@/util/functions/data/modifierPriceAdjFn';
import {
  primaryServiceBaseDuration,
} from './serviceDurationHelpers';
import {
  Bundle,
  DiscountCode,
} from '@/util/types/serverTypeCollection/discounts';

// Round to cents (two decimals) consistently to avoid floating-point artifacts
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

// Helper: Calculate discount amount (percent or fixed)
const calculateDiscount = (
  base: number,
  type: 'percent' | 'fixed',
  value: number,
  capAtBase = true
): number => {
  if (type === 'percent') {
    const fraction = value <= 1 ? value : value / 100;
    return round2(base * fraction);
  }
  const fixedAmount = round2(value);
  return capAtBase ? Math.min(base, fixedAmount) : fixedAmount;
};

// Helper: Recalculate tax and total for a service
const updateServicePricing = (
  service: ServiceWithPricing,
  newPreTax: number,
  discountAmount: number,
  discountType: 'primary' | 'bundle' | 'code'
): ServiceWithPricing => {
  let newTax = 0;
  if (service.tax && service.tax > 0) {
    if (service.taxType === 'percent') {
      newTax = round2(newPreTax * service.tax);
    } else {
      newTax = Math.min(newPreTax, round2(service.tax));
    }
  }

  const newTotal = round2(
    newPreTax + newTax + round2(service.serviceAreaCost || 0)
  );

  const updated = {
    ...service,
    preTaxAmount: round2(newPreTax),
    taxAmount: newTax,
    totalPrice: newTotal,
  };

  // Dynamically set the discount amount property
  if (discountType === 'primary') {
    updated.primaryDiscountAmount = round2(discountAmount);
  } else if (discountType === 'bundle') {
    updated.bundleDiscountAmount = round2(discountAmount);
  } else if (discountType === 'code') {
    updated.codeDiscountAmount = round2(discountAmount);
  }

  return updated;
};

export const calculateServicePrices = (
  services: (ServWithMods & { isPrimary: boolean })[],
  property: AddressForScheduler,
  serviceAreaCost?: number,
  requested: boolean = false,
  requiredInfoValues?: RequiredInfoValue[],
  equipmentQuantities?: EquipmentQuantity[],
  serviceIds?: string[],
  inspectorIds?: string[],
  options?: {
    bundle?: Bundle | null;
    discountCode?: DiscountCode | null;
  },
  serviceAreaIds?: string[]
): ServiceWithPricing[] => {
  // Preserve isPrimary from caller (usePriceCalculation assigns primary per canBePrimary)
  let servClone = [...services];

  const withPriceBase = servClone.map((service) => {
    const startingFee = service.isPrimary
      ? service.isPrimaryFee || service.fee
      : service.fee;
    const mods = modifierPriceAdjFn(
      { ...service, startingFee },
      property,
      requested,
      requiredInfoValues || [],
      equipmentQuantities || [],
      serviceIds || [],
      inspectorIds || [],
      serviceAreaIds || []
    );
    let basePrice = startingFee + ('finalPrice' in mods ? mods.finalPrice : 0);
    basePrice = round2(basePrice);

    // Calculate tax
    let taxAmount = 0;
    if (service.tax && service.tax > 0) {
      if (service.taxType === 'percent') {
        taxAmount = round2(basePrice * service.tax);
      } else {
        taxAmount = round2(service.tax);
      }
    }

    let totalPrice = round2(
      basePrice + taxAmount + (service.isPrimary ? serviceAreaCost || 0 : 0)
    );

    const finalCalculatedDuration =
      ('finalDuration' in mods ? mods.finalDuration : 0) +
      (service.isPrimary
        ? primaryServiceBaseDuration(
            service.duration,
            service.durationAsPrimary,
          )
        : service.duration);

    return {
      ...service,
      startingFee,
      modifierCalc: mods,
      preTaxAmount: round2(basePrice),
      taxAmount: round2(taxAmount),
      totalPrice: round2(totalPrice),
      serviceAreaCost: round2(service.isPrimary ? serviceAreaCost || 0 : 0),
      totalDuration: finalCalculatedDuration,
      modifierExplanation: 'finalPrice' in mods ? mods.explanation : undefined,
      originalPreTaxAmount: basePrice,
      originalPreDiscountTotal: totalPrice,
      bundleDiscountAmount: 0,
      codeDiscountAmount: 0,
      primaryDiscountAmount: 0,
      // New audit fields
      originalBasePrice: startingFee,
      modifiersTotal: 'finalPrice' in mods ? mods.finalPrice : undefined,
      areaFee: round2(service.isPrimary ? serviceAreaCost || 0 : 0),
    };
  });
  // Apply primary service discount: first eligible service gets discount, capped at base price
  let processedServices = withPriceBase as ServiceWithPricing[];
  const bundle = options?.bundle as any;
  if (bundle?._primaryServiceId?.length && bundle.primaryServiceDiscountType) {
    const eligibleIndex = processedServices.findIndex((s) =>
      bundle._primaryServiceId.includes(s._id)
    );
    if (eligibleIndex !== -1) {
      const service = processedServices[eligibleIndex];
      const base = round2(service.preTaxAmount || 0);
      const discount = calculateDiscount(
        base,
        bundle.primaryServiceDiscountType,
        bundle.primaryServiceDiscountValue || 0
      );

      if (discount > 0) {
        const newPreTax = Math.max(0, base - discount);
        processedServices[eligibleIndex] = updateServicePricing(
          service,
          newPreTax,
          discount,
          'primary'
        );
      }
    }
  }
  // Apply bundle discounts: uses original pre-tax amount as basis, capped at current pre-tax
  if (bundle?.items?.length) {
    processedServices = processedServices.map((service) => {
      const bundleItem = bundle.items.find(
        (item: any) => item._serviceId === service._id
      );
      if (!bundleItem) return service;

      const base = round2(
        service.originalPreTaxAmount || service.preTaxAmount || 0
      );
      const discount = calculateDiscount(
        base,
        bundleItem.discountType,
        bundleItem.discountValue || 0
      );
      const currentPreTax = round2(service.preTaxAmount || 0);
      const newPreTax = Math.max(0, currentPreTax - discount);

      return updateServicePricing(service, newPreTax, discount, 'bundle');
    });
  }
  // Apply discount code: percent splits proportionally, fixed splits evenly (allowing negatives)
  const code = options?.discountCode;
  if (code) {
    const eligibleServices = processedServices.filter((service) => {
      return (code._serviceIds?.length || 0) > 0
        ? code._serviceIds?.includes(service._id)
        : code.appliesToAllServices;
    });

    if (eligibleServices.length > 0) {
      const totalPreTax = round2(
        eligibleServices.reduce(
          (sum, s) => sum + round2(s.preTaxAmount || 0),
          0
        )
      );

      if (totalPreTax > 0 || code.discountType === 'fixed') {
        const totalDiscount = calculateDiscount(
          totalPreTax,
          code.discountType,
          code.discountValue || 0,
          false
        );

        if (code.discountType === 'percent') {
          if (totalPreTax === 0) return processedServices; // Can't split percentage of zero

          let allocated = 0;
          processedServices = processedServices.map((service, idx) => {
            if (!eligibleServices.some((s) => s._id === service._id))
              return service;

            const servicePreTax = round2(service.preTaxAmount || 0);
            const isLast =
              idx ===
              processedServices.findLastIndex((s) =>
                eligibleServices.some((e) => e._id === s._id)
              );

            const allocation = isLast
              ? round2(totalDiscount - allocated)
              : round2(totalDiscount * (servicePreTax / totalPreTax));

            allocated += allocation;
            const newPreTax = servicePreTax - allocation;

            return updateServicePricing(service, newPreTax, allocation, 'code');
          });
        } else {
          // Fixed discount: split evenly across eligible services
          const perService = round2(totalDiscount / eligibleServices.length);
          let remaining = totalDiscount;

          processedServices = processedServices.map((service, idx) => {
            if (!eligibleServices.some((s) => s._id === service._id))
              return service;

            const isLast =
              idx ===
              processedServices.findLastIndex((s) =>
                eligibleServices.some((e) => e._id === s._id)
              );
            const allocation = isLast ? remaining : perService;
            remaining -= allocation;

            const servicePreTax = round2(service.preTaxAmount || 0);
            const newPreTax = servicePreTax - allocation;

            return updateServicePricing(service, newPreTax, allocation, 'code');
          });
        }
      }
    }
  }

  return processedServices;
};

